import React, { useState, useEffect } from 'react';
import { Layout } from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { Settings as SettingsIcon, Mail, Server, Lock, Send, CheckCircle, XCircle, Bot, Sparkles, Key, Database, Download } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';

const API = '/api';

export const Settings = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [testingSmtp, setTestingSmtp] = useState(false);
  
  const [smtpSettings, setSmtpSettings] = useState({
    smtp_host: '',
    smtp_port: 587,
    smtp_user: '',
    smtp_password: '',
    smtp_from_email: '',
    smtp_from_name: 'Adsem Performance',
    smtp_use_tls: true,
    is_configured: false
  });
  
  const [aiSettings, setAiSettings] = useState({
    gemini_api_key: '',
    claude_api_key: '',
    gemini_enabled: true,
    claude_enabled: true,
    preferred_provider: 'gemini',
    gemini_model: 'gemini-2.0-flash-lite',
    claude_model: 'claude-3-5-haiku-20241022',
    is_configured: false
  });

  useEffect(() => {
    loadSmtpSettings();
    loadAiSettings();
  }, []);

  const loadSmtpSettings = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/settings/smtp`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSmtpSettings(prev => ({ ...prev, ...response.data }));
    } catch (error) {
      console.error('Failed to load SMTP settings');
    }
  };
  
  const loadAiSettings = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/settings/ai`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAiSettings(prev => ({ ...prev, ...response.data }));
    } catch (error) {
      console.error('Failed to load AI settings');
    }
  };

  const saveSmtpSettings = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/settings/smtp`, smtpSettings, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Setările SMTP au fost salvate');
      loadSmtpSettings();
    } catch (error) {
      toast.error('Eroare la salvarea setărilor SMTP');
    } finally {
      setLoading(false);
    }
  };
  
  const saveAiSettings = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/settings/ai`, aiSettings, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Setările AI au fost salvate');
      loadAiSettings();
    } catch (error) {
      toast.error('Eroare la salvarea setărilor AI');
    } finally {
      setLoading(false);
    }
  };

  const testSmtpConnection = async () => {
    setTestingSmtp(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/settings/smtp/test`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(response.data.message);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Eroare la testarea SMTP');
    } finally {
      setTestingSmtp(false);
    }
  };

  if (user?.role !== 'admin') {
    return (
      <Layout>
        <div className="flex items-center justify-center h-[60vh]">
          <Card className="p-8 text-center">
            <Lock className="w-16 h-16 mx-auto text-slate-300 mb-4" />
            <h2 className="text-xl font-semibold">Acces Restricționat</h2>
            <p className="text-muted-foreground mt-2">Doar administratorii pot accesa setările.</p>
          </Card>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6">
        <div>
          <h1 className="font-outfit text-4xl font-bold tracking-tight flex items-center gap-3">
            <SettingsIcon className="w-10 h-10" />
            Setări Sistem
          </h1>
          <p className="mt-2 text-muted-foreground">Configurează setările platformei</p>
        </div>

        <Tabs defaultValue="ai" className="space-y-6">
          <TabsList>
            <TabsTrigger value="ai" className="flex items-center gap-2">
              <Bot className="w-4 h-4" />
              AI Assistant
            </TabsTrigger>
            <TabsTrigger value="smtp" className="flex items-center gap-2">
              <Mail className="w-4 h-4" />
              SMTP / Email
            </TabsTrigger>
            <TabsTrigger value="backup" className="flex items-center gap-2">
              <Database className="w-4 h-4" />
              Backup & Export
            </TabsTrigger>
          </TabsList>

          {/* AI Settings Tab */}
          <TabsContent value="ai">
            <Card>
              <CardHeader className="border-b">
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="flex items-center gap-2">
                      <Sparkles className="w-5 h-5 text-purple-500" />
                      Configurare AI Assistant
                    </CardTitle>
                    <p className="text-sm text-muted-foreground mt-1">
                      Configurează API keys proprii pentru Gemini și/sau Claude. Costurile sunt minime cu modelele selectate.
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    {aiSettings.is_configured ? (
                      <span className="flex items-center gap-1 text-green-600 text-sm">
                        <CheckCircle className="w-4 h-4" />
                        Configurat
                      </span>
                    ) : (
                      <span className="flex items-center gap-1 text-amber-600 text-sm">
                        <XCircle className="w-4 h-4" />
                        Neconfigurat
                      </span>
                    )}
                  </div>
                </div>
              </CardHeader>
              <CardContent className="pt-6">
                <div className="space-y-6 max-w-2xl">
                  <div className="p-4 bg-green-50 rounded-lg border border-green-100">
                    <h4 className="font-medium text-green-900 flex items-center gap-2">
                      💰 Costuri Estimate
                    </h4>
                    <p className="text-sm text-green-700 mt-1">
                      Cu modelele selectate (Gemini Flash Lite / Claude Haiku), costul estimat este <strong>sub $1/lună</strong> pentru utilizare normală (~100 cereri/zi).
                    </p>
                  </div>
                  
                  {/* Gemini Section */}
                  <div className="border rounded-lg p-4">
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center gap-2">
                        <span className="text-lg font-medium">Google Gemini</span>
                        <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded">Recomandat</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-sm text-muted-foreground">Activ</span>
                        <Switch
                          checked={aiSettings.gemini_enabled}
                          onCheckedChange={(v) => setAiSettings(s => ({ ...s, gemini_enabled: v }))}
                          data-testid="ai-gemini-toggle"
                        />
                      </div>
                    </div>
                    
                    {aiSettings.gemini_enabled && (
                      <div className="space-y-4">
                        <div>
                          <Label>API Key</Label>
                          <Input
                            type="password"
                            placeholder="AIza..."
                            value={aiSettings.gemini_api_key}
                            onChange={(e) => setAiSettings(s => ({ ...s, gemini_api_key: e.target.value }))}
                            data-testid="ai-gemini-key"
                          />
                          <p className="text-xs text-muted-foreground mt-1">
                            Obține gratuit de la <a href="https://aistudio.google.com/apikey" target="_blank" rel="noopener noreferrer" className="text-blue-500 hover:underline">Google AI Studio</a>
                          </p>
                        </div>
                        <div>
                          <Label>Model</Label>
                          <Select 
                            value={aiSettings.gemini_model} 
                            onValueChange={(v) => setAiSettings(s => ({ ...s, gemini_model: v }))}
                          >
                            <SelectTrigger>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="gemini-2.0-flash-lite">Gemini 2.0 Flash Lite (cel mai ieftin ~$0.075/1M tokens)</SelectItem>
                              <SelectItem value="gemini-2.0-flash">Gemini 2.0 Flash (rapid, ieftin)</SelectItem>
                              <SelectItem value="gemini-1.5-flash">Gemini 1.5 Flash (stabil)</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                    )}
                  </div>
                  
                  {/* Claude Section */}
                  <div className="border rounded-lg p-4">
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center gap-2">
                        <span className="text-lg font-medium">Anthropic Claude</span>
                        <span className="text-xs bg-purple-100 text-purple-700 px-2 py-0.5 rounded">Calitate superioară</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-sm text-muted-foreground">Activ</span>
                        <Switch
                          checked={aiSettings.claude_enabled}
                          onCheckedChange={(v) => setAiSettings(s => ({ ...s, claude_enabled: v }))}
                          data-testid="ai-claude-toggle"
                        />
                      </div>
                    </div>
                    
                    {aiSettings.claude_enabled && (
                      <div className="space-y-4">
                        <div>
                          <Label>API Key</Label>
                          <Input
                            type="password"
                            placeholder="sk-ant-..."
                            value={aiSettings.claude_api_key}
                            onChange={(e) => setAiSettings(s => ({ ...s, claude_api_key: e.target.value }))}
                            data-testid="ai-claude-key"
                          />
                          <p className="text-xs text-muted-foreground mt-1">
                            Obține de la <a href="https://console.anthropic.com/settings/keys" target="_blank" rel="noopener noreferrer" className="text-blue-500 hover:underline">Anthropic Console</a> ($5 credit inițial)
                          </p>
                        </div>
                        <div>
                          <Label>Model</Label>
                          <Select 
                            value={aiSettings.claude_model} 
                            onValueChange={(v) => setAiSettings(s => ({ ...s, claude_model: v }))}
                          >
                            <SelectTrigger>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="claude-3-5-haiku-20241022">Claude 3.5 Haiku (cel mai ieftin ~$0.25/1M tokens)</SelectItem>
                              <SelectItem value="claude-3-5-sonnet-20241022">Claude 3.5 Sonnet (echilibrat)</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                    )}
                  </div>
                  
                  <div>
                    <Label>Provider Preferat (când ambele sunt disponibile)</Label>
                    <Select 
                      value={aiSettings.preferred_provider} 
                      onValueChange={(v) => setAiSettings(s => ({ ...s, preferred_provider: v }))}
                    >
                      <SelectTrigger data-testid="ai-provider-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="gemini">Google Gemini (rapid, cost redus)</SelectItem>
                        <SelectItem value="claude">Anthropic Claude (calitate superioară)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <Button onClick={saveAiSettings} disabled={loading} className="w-full">
                    {loading ? 'Se salvează...' : 'Salvează Setările AI'}
                  </Button>
                  
                  {aiSettings.is_configured && (
                    <div className="pt-4 border-t">
                      <Button 
                        variant="outline" 
                        className="w-full"
                        onClick={() => window.location.href = '/ai-admin'}
                      >
                        <Bot className="w-4 h-4 mr-2" />
                        Deschide AI Admin Assistant
                      </Button>
                      <p className="text-xs text-center text-muted-foreground mt-2">
                        Pentru a da acces echipei la AI, mergi la <a href="/team" className="text-blue-500 hover:underline">Team</a> și editează permisiunile.
                      </p>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="smtp">
            <Card>
              <CardHeader className="border-b">
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="flex items-center gap-2">
                      <Server className="w-5 h-5" />
                      Configurare Server SMTP
                    </CardTitle>
                    <p className="text-sm text-muted-foreground mt-1">
                      Configurează serverul de email pentru notificări și resetare parolă
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    {smtpSettings.is_configured ? (
                      <span className="flex items-center gap-1 text-green-600 text-sm">
                        <CheckCircle className="w-4 h-4" />
                        Configurat
                      </span>
                    ) : (
                      <span className="flex items-center gap-1 text-amber-600 text-sm">
                        <XCircle className="w-4 h-4" />
                        Neconfigurat
                      </span>
                    )}
                  </div>
                </div>
              </CardHeader>
              <CardContent className="pt-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-4">
                    <div>
                      <Label>Server SMTP</Label>
                      <Input
                        placeholder="smtp.gmail.com"
                        value={smtpSettings.smtp_host}
                        onChange={(e) => setSmtpSettings(s => ({ ...s, smtp_host: e.target.value }))}
                      />
                      <p className="text-xs text-muted-foreground mt-1">Ex: smtp.gmail.com, smtp.office365.com</p>
                    </div>
                    
                    <div>
                      <Label>Port SMTP</Label>
                      <Input
                        type="number"
                        value={smtpSettings.smtp_port}
                        onChange={(e) => setSmtpSettings(s => ({ ...s, smtp_port: parseInt(e.target.value) }))}
                      />
                      <p className="text-xs text-muted-foreground mt-1">Uzual: 587 (TLS) sau 465 (SSL)</p>
                    </div>
                    
                    <div>
                      <Label>Utilizator SMTP</Label>
                      <Input
                        placeholder="email@domain.com"
                        value={smtpSettings.smtp_user}
                        onChange={(e) => setSmtpSettings(s => ({ ...s, smtp_user: e.target.value }))}
                      />
                    </div>
                    
                    <div>
                      <Label>Parolă SMTP</Label>
                      <Input
                        type="password"
                        placeholder="••••••••"
                        value={smtpSettings.smtp_password}
                        onChange={(e) => setSmtpSettings(s => ({ ...s, smtp_password: e.target.value }))}
                      />
                      <p className="text-xs text-muted-foreground mt-1">Pentru Gmail, folosește App Password</p>
                    </div>
                  </div>
                  
                  <div className="space-y-4">
                    <div>
                      <Label>Email Expeditor</Label>
                      <Input
                        placeholder="noreply@adsem.ro"
                        value={smtpSettings.smtp_from_email}
                        onChange={(e) => setSmtpSettings(s => ({ ...s, smtp_from_email: e.target.value }))}
                      />
                    </div>
                    
                    <div>
                      <Label>Nume Expeditor</Label>
                      <Input
                        placeholder="Adsem Performance"
                        value={smtpSettings.smtp_from_name}
                        onChange={(e) => setSmtpSettings(s => ({ ...s, smtp_from_name: e.target.value }))}
                      />
                    </div>
                    
                    <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                      <div>
                        <Label>Folosește TLS</Label>
                        <p className="text-xs text-muted-foreground">Conexiune securizată (recomandat)</p>
                      </div>
                      <Switch
                        checked={smtpSettings.smtp_use_tls}
                        onCheckedChange={(checked) => setSmtpSettings(s => ({ ...s, smtp_use_tls: checked }))}
                      />
                    </div>
                    
                    <div className="pt-4 space-y-3">
                      <Button onClick={saveSmtpSettings} disabled={loading} className="w-full">
                        {loading ? 'Se salvează...' : 'Salvează Setările'}
                      </Button>
                      
                      <Button 
                        onClick={testSmtpConnection} 
                        disabled={testingSmtp || !smtpSettings.is_configured}
                        variant="outline"
                        className="w-full"
                      >
                        <Send className="w-4 h-4 mr-2" />
                        {testingSmtp ? 'Se trimite...' : 'Trimite Email de Test'}
                      </Button>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Backup & Export Tab */}
          <TabsContent value="backup">
            <Card>
              <CardHeader className="border-b">
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="flex items-center gap-2">
                      <Database className="w-5 h-5 text-green-600" />
                      Backup & Export Date
                    </CardTitle>
                    <p className="text-sm text-muted-foreground mt-1">
                      Exportă toate datele aplicației pentru backup sau migrare
                    </p>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="pt-6">
                <div className="space-y-6 max-w-2xl">
                  {/* Database Export */}
                  <div className="border rounded-lg p-6">
                    <div className="flex items-start gap-4">
                      <div className="p-3 bg-green-100 rounded-lg">
                        <Database className="w-8 h-8 text-green-600" />
                      </div>
                      <div className="flex-1">
                        <h3 className="font-semibold text-lg">Export Bază de Date</h3>
                        <p className="text-sm text-muted-foreground mt-1">
                          Descarcă toate datele din aplicație în format JSON. Include:
                        </p>
                        <ul className="text-sm text-muted-foreground mt-2 space-y-1">
                          <li>• Utilizatori (fără parole)</li>
                          <li>• Proiecte, Task-uri, Time Entries</li>
                          <li>• Cereri Concediu</li>
                          <li>• Configurări Salarii</li>
                          <li>• Survey-uri și Evaluări Clienți</li>
                          <li>• Setări (cu date sensibile mascate)</li>
                        </ul>
                        <Button 
                          className="mt-4 bg-green-600 hover:bg-green-700"
                          onClick={() => {
                            const token = localStorage.getItem('token');
                            const link = document.createElement('a');
                            link.href = `${API}/admin/export-database`;
                            link.setAttribute('download', '');
                            // Create fetch request with auth
                            fetch(`${API}/admin/export-database`, {
                              headers: { 'Authorization': `Bearer ${token}` }
                            })
                            .then(res => res.blob())
                            .then(blob => {
                              const url = window.URL.createObjectURL(blob);
                              const a = document.createElement('a');
                              a.href = url;
                              a.download = `adsem_export_${new Date().toISOString().split('T')[0]}.json`;
                              document.body.appendChild(a);
                              a.click();
                              a.remove();
                              window.URL.revokeObjectURL(url);
                              toast.success('Export descărcat cu succes!');
                            })
                            .catch(err => {
                              console.error(err);
                              toast.error('Eroare la descărcarea exportului');
                            });
                          }}
                        >
                          <Download className="w-4 h-4 mr-2" />
                          Descarcă Export JSON
                        </Button>
                      </div>
                    </div>
                  </div>

                  {/* Export Complet pentru Analiza */}
                  <div className="border rounded-lg p-6 border-purple-200 bg-purple-50/40" data-testid="full-export-card">
                    <div className="flex items-start gap-4">
                      <div className="p-3 bg-purple-100 rounded-lg">
                        <Download className="w-8 h-8 text-purple-600" />
                      </div>
                      <div className="flex-1">
                        <h3 className="font-semibold text-lg">Export Complet pentru Analiză</h3>
                        <p className="text-sm text-muted-foreground mt-1">
                          Arhivă ZIP cu absolut toate datele înregistrate, gata pentru o analiză separată (ex: cu alt agent AI). Include:
                        </p>
                        <ul className="text-sm text-muted-foreground mt-2 space-y-1">
                          <li>• Proiecte cu valoare lunară, cost ore pontate și profit estimat</li>
                          <li>• Ore lucrate pe fiecare proiect și pe fiecare coleg</li>
                          <li>• Task-urile fiecărui coleg (status, ore estimate)</li>
                          <li>• Clienți, concedii, evaluări, salarii</li>
                          <li>• Fișiere CSV + JSON complet + SUMAR.txt cu agregări</li>
                        </ul>
                        <Button
                          className="mt-4 bg-purple-600 hover:bg-purple-700"
                          data-testid="full-export-download-btn"
                          onClick={() => {
                            const token = localStorage.getItem('token');
                            const tId = toast.loading('Se generează exportul complet din datele live...');
                            fetch(`${API}/admin/export-complet`, {
                              headers: { 'Authorization': `Bearer ${token}` }
                            })
                            .then(res => {
                              if (!res.ok) throw new Error('export failed');
                              return res.blob();
                            })
                            .then(blob => {
                              const url = window.URL.createObjectURL(blob);
                              const a = document.createElement('a');
                              a.href = url;
                              a.download = `adsem_export_complet_${new Date().toISOString().split('T')[0]}.zip`;
                              document.body.appendChild(a);
                              a.click();
                              a.remove();
                              window.URL.revokeObjectURL(url);
                              toast.success('Export complet descărcat!', { id: tId });
                            })
                            .catch(err => {
                              console.error(err);
                              toast.error('Eroare la descărcarea exportului complet', { id: tId });
                            });
                          }}
                        >
                          <Download className="w-4 h-4 mr-2" />
                          Descarcă Export Complet (ZIP)
                        </Button>
                      </div>
                    </div>
                  </div>

                  {/* Info Box */}
                  <div className="p-4 bg-blue-50 rounded-lg border border-blue-100">
                    <h4 className="font-medium text-blue-900 flex items-center gap-2">
                      ℹ️ Informații
                    </h4>
                    <p className="text-sm text-blue-700 mt-1">
                      Exportul nu include parolele utilizatorilor și cheile API sunt mascate pentru securitate.
                      Fișierul JSON poate fi folosit pentru backup sau pentru a migra datele către altă instanță.
                    </p>
                  </div>

                  {/* Code Export Info */}
                  <div className="border rounded-lg p-6 bg-slate-50">
                    <div className="flex items-start gap-4">
                      <div className="p-3 bg-slate-200 rounded-lg">
                        <Key className="w-8 h-8 text-slate-600" />
                      </div>
                      <div className="flex-1">
                        <h3 className="font-semibold text-lg">Export Cod Sursă</h3>
                        <p className="text-sm text-muted-foreground mt-1">
                          Pentru a exporta codul sursă al aplicației, folosește funcția 
                          <strong> "Save to GitHub"</strong> din platforma Emergent.
                        </p>
                        <p className="text-sm text-muted-foreground mt-2">
                          Aceasta va salva tot codul (frontend, backend) într-un repository GitHub.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </Layout>
  );
};

export default Settings;
